/**
 * The line-item reducer bodies, as plain functions on the draft shipment.
 *
 * They're here rather than inline in the slice because each carries a rule
 * worth reading on its own — deriving a freight class from density, and
 * swapping a length and width that were entered the wrong way round.
 */

import { lineItemsRecommendFreightClassFromDimensions } from "@/Shared/LineItems/Helpers/lineItemsRecommendFreightClassFromDimensions";
import type { AddLineItemType } from "@/Shared/Types/lineItemTypes";
import type { UUID } from "@/Shared/types";
import type { Patch } from "@freightsimple/shared/types";
import type { LineItem } from "@freightsimple/generated-dashboard-openapi-client/models/LineItem";
import { v4 as uuidv4 } from "uuid";
import type { BookShipmentShipmentType } from "@/Shared/Types/bookShipmentSliceTypes";

export function bookShipmentDuplicateLineItem(
  shipment: BookShipmentShipmentType,
  lineItemId: UUID,
) {
  const lineItemToDuplicate = shipment.lineItems.find((lineItem) => {
    return lineItem.lineItemId === lineItemId;
  });

  if (lineItemToDuplicate) {
    const duplicatedLineItem = {
      ...lineItemToDuplicate,
      lineItemId: uuidv4(),
    };
    shipment.lineItems.push(duplicatedLineItem);
  }
}

export function bookShipmentUpdateLineItem(
  shipment: BookShipmentShipmentType,
  lineItemId: UUID,
  value: Patch<LineItem>,
) {
  const lineItemIndex = shipment.lineItems.findIndex((lineItem) => {
    return lineItem.lineItemId === lineItemId;
  });

  const existingLineItem = shipment.lineItems[lineItemIndex];
  if (lineItemIndex === -1 || existingLineItem === undefined) {
    return;
  }

  shipment.lineItems[lineItemIndex] = {
    ...existingLineItem,
    ...value,
  } as AddLineItemType;

  const li = shipment.lineItems[lineItemIndex];
  if (
    li !== undefined &&
    (li.nmfcItemNumber === undefined || li.freightClass === undefined)
  ) {
    if (
      li.weightPerHandlingUnit !== undefined &&
      li.length !== undefined &&
      li.width !== undefined &&
      li.height !== undefined
    ) {
      // With no NMFC code, we calculate the Freight Class.
      // With an NMFC code but no class yet (the NMFC lookup is async and
      // may not return one), density stands in so booking is never
      // blocked on a field we derive ourselves; a class returned by the
      // lookup arrives via this same reducer and, once set, is kept.
      li.freightClass = lineItemsRecommendFreightClassFromDimensions(
        li.weightPerHandlingUnit,
        li.length,
        li.width,
        li.height,
      ).freightClass;
    }
  }
}

// If someone enters, say, 30x60x48 it is better if we treat this as 60x30x48
// We need to be careful not to rearrange as they are typing though, eg if they
// enter 60x300 but the final zero is a typo, which they would immediately delete
// So we should only run this when all the values are correctly entered
export function bookShipmentRearrangeLengthWidth(
  shipment: BookShipmentShipmentType,
  lineItemId: UUID,
) {
  const lineItemIndex = shipment.lineItems.findIndex((lineItem) => {
    return lineItem.lineItemId === lineItemId;
  });

  const lineItem = shipment.lineItems[lineItemIndex];

  if (lineItem === undefined) {
    console.error("No lineItem");
    return;
  }

  if (
    lineItem !== undefined &&
    lineItem.width !== undefined &&
    lineItem.length !== undefined
  ) {
    if (lineItem.width > lineItem.length) {
      const newLineItem = {
        ...lineItem,
        width: lineItem.length,
        length: lineItem.width,
      };

      // We need to swap
      shipment.lineItems[lineItemIndex] = newLineItem;
    }
  }
}
